import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import useId from "@mui/material/utils/useId";
import themeConfig from "../../../theme/themeConfig.json";

interface TableFilterOption {
  value: string | number;
  label: string | number;
}

interface Props {
  name: string;
  label: string;
  value: string | number;
  options: TableFilterOption[];
  fallbackOptionIndex?: number;
  onChange: (value: string) => void;
}

/** Keep option widths outside the table's column sizing calculation. */
export default function TableFilterSelect({
  name,
  label,
  value,
  options,
  fallbackOptionIndex = 0,
  onChange,
}: Props) {
  const controlId = useId();
  return (
    <Select
      id={controlId}
      name={name}
      value={String(value)}
      onChange={(event) => onChange(String(event.target.value))}
      fullWidth
      autoWidth
      displayEmpty
      data-control={`select-${name}`}
      inputProps={{ "aria-label": label }}
      renderValue={(selected) =>
        options.find((option) => String(option.value) === selected)?.label ??
        (selected === ""
          ? (options[fallbackOptionIndex] ?? options[0])?.label ?? ""
          : "")
      }
      sx={{
        minWidth: 0,
        height: "34px",
        borderRadius: themeConfig.radii.control,
        backgroundColor: themeConfig.colors.surface.DEFAULT,
        color: themeConfig.colors.muted.dark,
        fontSize: "0.875rem",
        fontWeight: 500,
        textAlign: "left",
        "& .MuiOutlinedInput-notchedOutline": { border: 0 },
        "& .MuiSelect-select": {
          minWidth: "0 !important",
          minHeight: "0 !important",
          height: "100%",
          boxSizing: "border-box",
          padding: "0 32px 0 12px",
          lineHeight: "34px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textAlign: "left",
          whiteSpace: "nowrap",
          borderRadius: themeConfig.radii.control,
          "&:focus": {
            backgroundColor: themeConfig.colors.surface.DEFAULT,
            borderRadius: themeConfig.radii.control,
          },
        },
        "& .MuiSelect-icon": {
          right: "8px",
          color: themeConfig.colors.muted.dark,
        },
      }}
      MenuProps={{
        // The portal prevents the table's horizontal scroller clipping options.
        disablePortal: false,
        marginThreshold: 16,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        PaperProps: {
          sx: {
            width: "max-content",
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "min(360px, calc(100dvh - 32px))",
            overflowX: "hidden",
            borderRadius: themeConfig.radii.control,
            "& .MuiMenuItem-root": {
              minHeight: "34px",
              fontSize: "0.95rem",
              fontWeight: 500,
              lineHeight: 1.5,
              textAlign: "left",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            },
          },
        },
        MenuListProps: { "aria-label": label },
      }}>
      {options.map((option) => (
        <MenuItem key={option.value} value={String(option.value)}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
}
